import { CSSObject } from '@emotion/react';
import { theme } from 'styles';
import { mobile } from 'styles/media';

const { color, radius, shadow } = theme;

const page: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  '@supports (height: 100dvh)': { height: '100dvh' },
  background: color.white,
  [mobile]: { height: 'auto', minHeight: '100dvh' },
};

const topBar: CSSObject = {
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 60,
  padding: '0 20px 0 12px',
  borderBottom: `1px solid ${color.grey100}`,
  background: color.white,
  [mobile]: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    padding: '0 12px 0 4px',
  },
};

const backButton: CSSObject = {
  flex: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  border: 'none',
  borderRadius: radius.md,
  background: 'transparent',
  color: color.grey700,
  cursor: 'pointer',
  ':hover': { background: color.grey75 },
};

const topTitle: CSSObject = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  h1: {
    fontSize: 18,
    fontWeight: 700,
    color: color.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  p: {
    fontSize: 12,
    color: color.textMuted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const saveButton: CSSObject = {
  height: 40,
  minWidth: 96,
  fontSize: 15,
};

const body: CSSObject = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  [mobile]: { flexDirection: 'column' },
};

const panel = (side: 'left' | 'right'): CSSObject => ({
  flex: 'none',
  width: side === 'left' ? 240 : 280,
  boxSizing: 'border-box',
  overflowY: 'auto',
  background: color.white,
  [side === 'left' ? 'borderRight' : 'borderLeft']:
    `1px solid ${color.grey100}`,
  [mobile]: {
    width: '100%',
    overflow: 'visible',
    border: 'none',
    borderTop: `8px solid ${color.grey75}`,
    order: side === 'left' ? 2 : 3,
  },
});

const section: CSSObject = {
  padding: '20px 16px',
  borderBottom: `1px solid ${color.grey75}`,
  ':last-of-type': { borderBottom: 'none' },
};

const sectionTitle: CSSObject = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
  fontSize: 14,
  fontWeight: 700,
  color: color.text,
  small: { fontSize: 13, fontWeight: 500, color: color.textMuted },
};

const groupTitle: CSSObject = {
  margin: '16px 0 8px',
  fontSize: 12,
  fontWeight: 600,
  color: color.textMuted,
};

const canvas: CSSObject = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '32px 24px',
  overflowY: 'auto',
  background: color.grey75,
  [mobile]: { order: 1, padding: '20px 16px', overflow: 'visible' },
};

const phone = (backgroundColor: string): CSSObject => ({
  width: 375,
  maxWidth: '100%',
  minHeight: 640,
  paddingBottom: 24,
  background: backgroundColor,
  borderRadius: radius.lg,
  boxShadow: shadow.popover,
  overflow: 'hidden',
  [mobile]: { minHeight: 480 },
});

const emptyPhone: CSSObject = {
  padding: '120px 24px',
  textAlign: 'center',
  fontSize: 14,
  color: color.textMuted,
};

const colorRow: CSSObject = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const swatchButton: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  height: 40,
  padding: '0 12px 0 8px',
  border: `1px solid ${color.grey100}`,
  borderRadius: radius.md,
  background: color.white,
  fontSize: 14,
  fontFamily: 'monospace',
  color: color.grey700,
  cursor: 'pointer',
  ':hover': { borderColor: color.grey200 },
};

const swatch = (value: string): CSSObject => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: value,
  boxShadow: `inset 0 0 0 1px ${color.grey200}`,
});

const sketchPopover: CSSObject = {
  position: 'absolute',
  top: 48,
  left: 0,
  zIndex: 10,
  borderRadius: radius.md,
  boxShadow: shadow.popover,
};

export {
  page,
  topBar,
  backButton,
  topTitle,
  saveButton,
  body,
  panel,
  section,
  sectionTitle,
  groupTitle,
  canvas,
  phone,
  emptyPhone,
  colorRow,
  swatchButton,
  swatch,
  sketchPopover,
};
